import cloneDeep from 'lodash/cloneDeep'
import {AttachmentInfo, IFeatureLayerInfo, IQuery} from '../../interfaces/models'
import {Extent, Feature, FeatureLayerInfo, FeatureStatus, VectorLayer} from '../../models'

import Axios from 'axios'
import appConfig from '../../../reducers/appConfig'
import {xyGisClient} from './xyGisClient'
import {CoordinateSystem} from '../../enums/CoordinateSystem'
import {GeometryType} from '../../enums/GeometryType'
import {
  IAppyEditsPrePayload,
  ISingleSourceXyGisApplyEditsPayload,
  ISourceApplyEdits,
  ITypeFeaturesPair,
  IXyApplyEditsResult,
  IXyNewFeaturesPayload,
  IXyUpdateFeaturesPayload,
  IXyAttachmentInfo2
} from '../../interfaces/services'
import {config} from '../../reducers/config'
import {getMapService} from '../../reducers/map'
import {XyParser} from './XyParser'

export class XyGisCrudService {
  public xyParser: XyParser
  constructor(xyParser: XyParser) {
    this.xyParser = xyParser
  }

  protected updateFeaturesToAttributes(features: Feature[], layerInfo: FeatureLayerInfo): IXyUpdateFeaturesPayload[] {
    return features.map(g => {
      return this.xyParser.featureToAttributes(g, layerInfo)
    })
  }

  protected newFeaturesAttributes(features: Array<Feature>, layerInfo: FeatureLayerInfo): IXyNewFeaturesPayload {
    const featuresPayload: IXyNewFeaturesPayload = {}
    features.forEach(f => {
      featuresPayload[f.guid] = this.xyParser.featureToAttributes(f, layerInfo)
    })
    return featuresPayload
  }

  public execute(layerInfo: FeatureLayerInfo, query: IQuery): Promise<Array<Feature>> {
    const modquery: IQuery = {...query}
    if (modquery.geometry) {
      ;(modquery as any).geometry = this.xyParser.geometryToAttribute(GeometryType.Polygon, query.geometry)
    }
    return xyGisClient
      .query<unknown>(layerInfo.sourceDescription.url, modquery)
      .then(data => this.xyParser.parseFeatures(layerInfo, data))
  }

  public executeForCount(layerInfo: FeatureLayerInfo, query: IQuery): Promise<number> {
    const modquery: IQuery = {...query}
    if (modquery.geometry) {
      ;(modquery as any).geometry = this.xyParser.geometryToAttribute(GeometryType.Polygon, query.geometry)
    }
    return xyGisClient.queryCount(layerInfo.sourceDescription.url, modquery)
  }

  public identify(
    featureLayerInfo: FeatureLayerInfo,
    outSR: CoordinateSystem,
    extent: Extent
  ): Promise<{layerInfo: FeatureLayerInfo; features: Array<Feature>; error?: Error}> {
    const ext = extent.getExtent()
    return new Promise((resolve, reject) => {
      const map = getMapService()
      const layer = featureLayerInfo.getLayer(map.getZoom())
      let hasMeasure = false
      if (layer && layer instanceof VectorLayer) {
        const result: Array<Feature> = []

        const pixel = map.getPixelFromCoordinate(extent.getCenter().getCoordinates())
        map.forEachFeatureAtPixel(pixel, (f, layer) => {
          if (layer.moreInfo.urls[0] == featureLayerInfo.url) {
            result.push(f)
          }
          if (layer.moreInfo.urls[0] == appConfig.reg.measures.url) {
            hasMeasure = true
          }
        })
        if (result.length == 0) {
          layer.getSource().forEachFeatureIntersectingExtent(ext, (f: any) => {
            result.push(f)
          })
        }
        if (hasMeasure) {
          resolve({layerInfo: featureLayerInfo, features: []})
        } else {
          resolve({layerInfo: featureLayerInfo, features: result})
        }
      } else {
        const layerInfo = featureLayerInfo
        const query: IQuery = {
          where: featureLayerInfo.sourceDescription.constantSqlWhere,
          geometry: extent.getPolygon()
        }
        this.execute(layerInfo, query).then(r => {
          resolve({layerInfo: featureLayerInfo, features: r})
        })
      }
    })
  }

  public attachAttachments(typeFeaturesPair: ITypeFeaturesPair): Promise<Feature[]> {
    return new Promise((resolve, reject) => {
      const allIds: string[] = []
      const idFeatures: {[id: string]: Feature} = {}
      typeFeaturesPair.features.forEach(f => {
        const tags = []
        if (f.getProperties().EKATTE) {
          tags.push(`екатте:${f.getProperties().EKATTE}`)
        }
        if (f.getProperties().ParcelNumber) {
          tags.push(`упи:${f.getProperties().ParcelNumber}`)
        }
        if (f.getProperties().QuarterNumber) {
          tags.push(`квартал:${f.getProperties().QuarterNumber}`)
        }

        const data = JSON.stringify(tags)
        const apiUrl = process.env.HOST_API_URL + '/GetFilesByTags'
        const accessToken = localStorage.getItem('accessToken')
        const accessTokenWithoutQuotes = accessToken && accessToken.replace(/['"]+/g, '')

        const header = accessToken
          ? {'Content-Type': 'application/json', Authorization: `Bearer ${accessTokenWithoutQuotes}`}
          : {'Content-Type': 'application/json'}
        const config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: apiUrl,
          headers: header,
          data: data
        }

        try {
          Axios.request(config)
            .then(result => {
              const featureId = f.getProperties()[typeFeaturesPair.layerInfo.sourceDescription.idField]
              const target = idFeatures[featureId]
              // The feature may have no id (logged below), or it may not have
              // its `attachments` map initialised — guard either way so a
              // malformed feature doesn't tear the whole batch down.
              if (target && target.attachments) {
                result.data.forEach((attachInfo: IXyAttachmentInfo2) => {
                  target.attachments[attachInfo.Name] = {
                    name: attachInfo.Name,
                    size: attachInfo.Size,
                    src: typeFeaturesPair.layerInfo.sourceDescription.baseUrl + '/DownloadFile/' + attachInfo.Path,
                    type: attachInfo.Type
                  }
                })
              }
              resolve(typeFeaturesPair.features)
            })
            .catch(error => {
              console.log(error)
            })
        } catch (e) {
          console.log('ERROR: ', e)
        }

        const id = f.getProperties()[typeFeaturesPair.layerInfo.sourceDescription.idField]
        if (!id) {
          console.warn("There shouldn't ne any null ids when calling attachAttachments!!!")
        } else {
          idFeatures[id.toString()] = f
          allIds.push(id)
        }
      })
    })
  }

  protected getFeaturesApplyEditsPayload(
    layerInfo: FeatureLayerInfo,
    adds?: Array<Feature>,
    updates?: Array<Feature>,
    deletes?: Array<Feature>
  ): ISingleSourceXyGisApplyEditsPayload {
    return {
      adds: adds ? this.newFeaturesAttributes(adds, layerInfo) : undefined,
      updates: updates ? this.updateFeaturesToAttributes(updates, layerInfo) : undefined,
      deletes: deletes ? deletes.map(g => g.getProperties()[layerInfo.sourceDescription.idField]) : undefined
    }
  }

  protected attachmentInfosToFiles(applyEdits: ISourceApplyEdits[]): {
    files: AttachmentInfo[]
    filesToRemove: string[]
  } {
    const attInfos: {files: AttachmentInfo[]; filesToRemove: string[]} = {
      files: [],
      filesToRemove: []
    }

    const nameSpaceAttachmentInfo = (
      feature: Feature,
      attInfo: AttachmentInfo,
      layerInfo: FeatureLayerInfo
    ): AttachmentInfo => {
      return {
        ...attInfo,
        name: `${layerInfo.sourceDescription.nameSpace}:${layerInfo.sourceDescription.layerName}:${
          feature.getProperties()[layerInfo.sourceDescription.idField]
        }:${attInfo.name}`
      }
    }

    const guidAttachmentInfo = (feature: Feature, attInfo: AttachmentInfo): AttachmentInfo => {
      return {...attInfo, name: feature.guid + ':' + attInfo.name}
    }

    const apply = (feature: Feature, layerInfo: FeatureLayerInfo, isNewFeature: boolean) => {
      for (var key in feature.attachmentChanges.adds) {
        attInfos.files.push(
          isNewFeature
            ? guidAttachmentInfo(feature, feature.attachmentChanges.adds[key])
            : nameSpaceAttachmentInfo(feature, feature.attachmentChanges.adds[key], layerInfo)
        )
      }
      for (var key in feature.attachmentChanges.updates) {
        attInfos.files.push(nameSpaceAttachmentInfo(feature, feature.attachmentChanges.updates[key], layerInfo))
      }
      for (var key in feature.attachmentChanges.deletes) {
        const src = feature.attachmentChanges.deletes[key].src
        if (src) {
          attInfos.filesToRemove.push(src)
        }
      }
    }
    applyEdits.forEach(a => {
      if (a.adds) {
        a.adds.forEach(changedEntity => {
          apply(changedEntity, a.layerInfo, true)
        })
      }
      if (a.updates) {
        a.updates.forEach(changedEntity => {
          apply(changedEntity, a.layerInfo, false)
        })
      }
    })
    return attInfos
  }

  public applyEdits(applyEdits: ISourceApplyEdits[]): Promise<ISourceApplyEdits[]> {
    const ekatte = applyEdits[0].adds && (applyEdits[0].adds[0] as any)?.values_?._SETTLEMENT?.values_?.EKATTE
    const settlementId = applyEdits[0].adds && applyEdits[0].adds[0]?.guid

    return new Promise((resolve, reject) => {
      const attachmentsStuff = this.attachmentInfosToFiles(applyEdits)
      const appyEditsPrePayload: IAppyEditsPrePayload = {
        FilesToRemove: attachmentsStuff.filesToRemove,
        Features: {},
        Files: attachmentsStuff.files
      }
      const adds: {[namespace: string]: {[featureType: string]: {[guid: string]: Feature}}} = {}
      applyEdits.forEach(a => {
        if (!adds[a.layerInfo.sourceDescription.nameSpace]) {
          adds[a.layerInfo.sourceDescription.nameSpace] = {}
        }
        if (!adds[a.layerInfo.sourceDescription.nameSpace][a.layerInfo.sourceDescription.layerName]) {
          adds[a.layerInfo.sourceDescription.nameSpace][a.layerInfo.sourceDescription.layerName] = {}
        }
        if (a.adds) {
          a.adds.forEach(add => {
            adds[a.layerInfo.sourceDescription.nameSpace][a.layerInfo.sourceDescription.layerName][add.guid] = add
          })
        }
        if (!appyEditsPrePayload.Features[a.layerInfo.sourceDescription.nameSpace]) {
          appyEditsPrePayload.Features[a.layerInfo.sourceDescription.nameSpace] = {}
        }
        if (
          !appyEditsPrePayload.Features[a.layerInfo.sourceDescription.nameSpace][
            a.layerInfo.sourceDescription.layerName
          ]
        ) {
          appyEditsPrePayload.Features[a.layerInfo.sourceDescription.nameSpace][
            a.layerInfo.sourceDescription.layerName
          ] = this.getFeaturesApplyEditsPayload(a.layerInfo, a.adds, a.updates, a.deletes)
        }
      })

      const newPayload = cloneDeep(appyEditsPrePayload)
      if (newPayload.Features?.Reg?.Quarter?.adds && settlementId && ekatte) {
        newPayload.Features.Reg.Quarter.adds[settlementId].EKATTE = ekatte
      }

      this.postEdits(newPayload)
        .then(r => {
          this.afterPostEdits(r, adds, applyEdits, resolve)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  protected convertAttachmentLinks(feats: Feature[], layerInfo: IFeatureLayerInfo) {
    const ns =
      layerInfo.sourceDescription.baseUrl +
      `/${layerInfo.sourceDescription.nameSpace}/${layerInfo.sourceDescription.layerName}/`
    feats.forEach(feat => {
      for (const name in feat.attachmentChanges.adds) {
        feat.attachmentChanges.adds[name].file = undefined
        feat.attachmentChanges.adds[name].src =
          ns + `${feat.getId()}/GetAttachment/` + encodeURIComponent(feat.attachmentChanges.adds[name].name)
        feat.attachments[name] = feat.attachmentChanges.adds[name]
      }
      for (const name in feat.attachmentChanges.updates) {
        feat.attachmentChanges.updates[name].file = undefined
        feat.attachmentChanges.updates[name].src =
          ns + `${feat.getId()}/GetAttachment/` + encodeURIComponent(feat.attachmentChanges.updates[name].name)
        feat.attachments[name] = feat.attachmentChanges.updates[name]
      }
      for (const name in feat.attachmentChanges.deletes) {
        delete feat.attachments[name]
      }
    })
  }

  protected afterPostEdits(
    r: IXyApplyEditsResult,
    adds: {[namespace: string]: {[featureType: string]: {[guid: string]: Feature}}},
    applyEdits: ISourceApplyEdits[],
    resolve: (r: any) => void
  ) {
    for (const namespace in r) {
      for (const featureType in r[namespace]) {
        for (const guid in r[namespace][featureType]) {
          const id = r[namespace][featureType][guid]
          const feature = adds[namespace][featureType][guid]
          feature.setId(id)
        }
      }
    }

    applyEdits.forEach(a => {
      this.convertAttachmentLinks(a.adds || [], a.layerInfo)
      this.convertAttachmentLinks(a.updates || [], a.layerInfo)
      if (a.adds)
        a.adds.forEach(a => {
          a.status = FeatureStatus.Unchanged
          a.attachmentChanges = {adds: {}, deletes: {}, updates: {}}
        })
      if (a.updates)
        a.updates.forEach(a => {
          a.status = FeatureStatus.Unchanged
          a.attachmentChanges = {adds: {}, deletes: {}, updates: {}}
        })
      if (a.deletes)
        a.deletes.forEach(a => {
          a.status = FeatureStatus.Deleted
          a.attachmentChanges = {adds: {}, deletes: {}, updates: {}}
        })
    })
    applyEdits.forEach(a => a.layerInfo.refresh())
    setTimeout(() => {
      resolve(applyEdits)
    }, 500)
  }

  protected postEdits(appyEditsPayload: IAppyEditsPrePayload): Promise<IXyApplyEditsResult> {
    const form = new FormData()
    appyEditsPayload.Files.forEach(file => {
      form.append('Files', file.file!, file.name)
    })
    form.append('Features', JSON.stringify(appyEditsPayload.Features))
    form.append('FilesToRemove', JSON.stringify(appyEditsPayload.FilesToRemove))
    // TODO there should be baseUrl more than one
    return xyGisClient.applyEdits<IXyApplyEditsResult>(config.getConfig().urls.xyApiUrl, form)
  }
}
